import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import queryString from 'query-string'
import { getLoginInfo, openLoginTopLevel } from 'loginControl'
import { getMimicTokenFromCookie } from 'modules/mimicToken'
import { getAxiosDefaultConfig } from 'utils'
import {
  baseUrl,
  claimWoocommerceSiteUrl,
  reconnectWoocommerceSiteUrl,
  verifyWoocommerceLogin,
} from 'urls'

type PluginPayload = {
  store_url?: string
  admin_email?: string
  page?: string
  store_less?: boolean
  ts?: string
  sig?: string
  /** Flag only — secrets must come via postMessage, never the iframe query string. */
  reconnect?: boolean
  plugin_version?: string
}

type OrgSummary = {
  id: string
  name: string
  workspace_user_email: string
}

type LinkStatus =
  | 'loading'
  | 'need_login'
  | 'need_org_switch'
  | 'claiming'
  | 'reconnecting'
  | 'error'
  | 'done'

function parsePluginPayload(): PluginPayload | null {
  try {
    const query = queryString.parse(window.location.search)
    const encoded = typeof query.payload === 'string' ? query.payload : ''
    if (!encoded) return null
    const parsed = JSON.parse(atob(encoded)) as PluginPayload & {
      private_key?: string
      api_token?: string
    }
    // Defense in depth: never treat secrets from the query string as credentials.
    delete parsed.private_key
    delete parsed.api_token
    return parsed
  } catch (error) {
    console.error('Failed to parse WooCommerce link payload', error)
    return null
  }
}

function openPayhelmTopLevel(path = '/dashboard') {
  const target = path.startsWith('/') ? path : `/${path}`
  window.open(target, '_top')
}

/** HMAC-only payload for /verify — never include private_key/api_token in the query. */
function buildHmacOnlyPayload(payload: PluginPayload): string {
  const safe: PluginPayload = {
    store_url: payload.store_url,
    admin_email: payload.admin_email,
    page: payload.page,
    store_less: payload.store_less,
    ts: payload.ts,
    sig: payload.sig,
  }
  if (payload.reconnect) safe.reconnect = true
  return btoa(JSON.stringify(safe))
}

async function finishVerifyAndDashboard(payload: PluginPayload) {
  const encoded = buildHmacOnlyPayload(payload)
  await axios.get(
    `${verifyWoocommerceLogin}?${queryString.stringify({
      channel: 'woocommerce',
      payload: encoded,
    })}`,
    getAxiosDefaultConfig(),
  )
  const next = new URLSearchParams(window.location.search)
  next.set('payload', encoded)
  window.location.replace(`/dashboard?${next.toString()}`)
}

type ReconnectCredentials = {
  private_key: string
  api_token?: string
  store_url?: string
  store_less?: boolean
  plugin_version?: string
  consumer_key?: string
  consumer_secret?: string
}

/** Allowed WP parent origins for reconnect postMessage (https preferred). */
function parentOriginsForStore(storeUrl?: string): string[] {
  if (!storeUrl) return []
  const host = String(storeUrl)
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .toLowerCase()
  if (!host) return []
  const origins = [`https://${host}`]
  // Local / private hosts may serve wp-admin over http.
  if (
    host === 'localhost' ||
    host.endsWith('.local') ||
    host.endsWith('.test') ||
    /^\d+\.\d+\.\d+\.\d+$/.test(host)
  ) {
    origins.push(`http://${host}`)
  }
  return origins
}

/**
 * Ask the WP admin parent for reconnect secrets via postMessage.
 * Secrets must never live in the iframe URL /verify payload.
 * targetOrigin is restricted to the plugin store host (not '*').
 */
function requestReconnectCredentialsFromParent(
  storeUrl?: string,
  timeoutMs = 8000,
): Promise<ReconnectCredentials> {
  return new Promise((resolve, reject) => {
    if (window.parent === window) {
      reject(new Error('Reconnect must run inside the WordPress plugin iframe.'))
      return
    }
    const allowedOrigins = parentOriginsForStore(storeUrl)
    if (!allowedOrigins.length) {
      reject(new Error('Missing store_url for reconnect credential handshake.'))
      return
    }
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new Error(
          'Timed out waiting for reconnect credentials from WordPress. Update the PayHelm plugin to 1.1.16+ and try again.',
        ),
      )
    }, timeoutMs)

    function onMessage(event: MessageEvent) {
      if (!allowedOrigins.includes(event.origin)) return
      const data = event.data
      if (!data || data.type !== 'payhelm-reconnect-credentials') return
      if (!data.private_key) return
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      resolve({
        private_key: String(data.private_key),
        api_token: data.api_token ? String(data.api_token) : undefined,
        store_url: data.store_url ? String(data.store_url) : undefined,
        store_less: data.store_less,
        plugin_version: data.plugin_version ? String(data.plugin_version) : undefined,
        consumer_key: data.consumer_key ? String(data.consumer_key) : undefined,
        consumer_secret: data.consumer_secret ? String(data.consumer_secret) : undefined,
      })
    }

    window.addEventListener('message', onMessage)
    // Post to each allowed origin — wrong scheme fails silently with a single target.
    for (const origin of allowedOrigins) {
      window.parent.postMessage({ type: 'payhelm-reconnect-credentials-request' }, origin)
    }
  })
}

/**
 * Store-less WordPress claim / reconnect screen.
 */
export default function LinkWordpressSite() {
  const payload = useMemo(() => parsePluginPayload(), [])
  const [status, setStatus] = useState<LinkStatus>('loading')
  const [error, setError] = useState('')
  const [organizations, setOrganizations] = useState<OrgSummary[]>([])

  const runClaim = useCallback(
    async (organizationId?: string) => {
      if (!payload?.store_url || payload.ts == null || !payload.sig) {
        setStatus('error')
        setError('Missing store link payload from the WordPress plugin.')
        return
      }
      if (getMimicTokenFromCookie()) {
        setStatus('need_login')
        return
      }
      const { app_token } = getLoginInfo()
      if (!app_token) {
        setStatus('need_login')
        return
      }

      // Reconnect: secrets come from WP parent via postMessage, never from the URL payload.
      if (payload.reconnect) {
        setStatus('reconnecting')
        try {
          const creds = await requestReconnectCredentialsFromParent(payload.store_url)
          await axios.post(
            reconnectWoocommerceSiteUrl,
            {
              store_url: payload.store_url,
              private_key: creds.private_key,
              api_token: creds.api_token,
              plugin_version: creds.plugin_version || payload.plugin_version,
              store_less: creds.store_less ?? payload.store_less,
              consumer_key: creds.consumer_key,
              consumer_secret: creds.consumer_secret,
            },
            getAxiosDefaultConfig(),
          )
          setStatus('done')
          await finishVerifyAndDashboard(payload)
        } catch (err) {
          console.error('reconnect failed', err)
          const data = err?.response?.data
          const code = data?.error
          if (code === 'owned_by_other') {
            setError(
              data?.message ||
                'Only the PayHelm account that owns this site can reconnect it. Ask the owner to disconnect or transfer the site.',
            )
            setStatus('error')
            return
          }
          setError(
            data?.message ||
              code ||
              err?.message ||
              'Unable to reconnect this WordPress site.',
          )
          setStatus('error')
        }
        return
      }

      setStatus('claiming')
      try {
        await axios.post(
          claimWoocommerceSiteUrl,
          {
            store_url: payload.store_url,
            ts: payload.ts,
            sig: payload.sig,
            ...(organizationId ? { organization_id: organizationId } : {}),
          },
          getAxiosDefaultConfig(),
        )
        setStatus('done')
        await finishVerifyAndDashboard(payload)
      } catch (err) {
        console.error('claim-site failed', err)
        const data = err?.response?.data
        const code = data?.error
        if (code === 'organization_switch_required') {
          setOrganizations(Array.isArray(data?.organizations) ? data.organizations : [])
          setStatus('need_org_switch')
          return
        }
        if (typeof code === 'string' && code.toLowerCase().includes('mimicking')) {
          setStatus('need_login')
          return
        }
        if (code === 'plugin_key_mismatch') {
          setError(
            data?.message ||
              'Plugin key does not match PayHelm. Sign in as the site owner to reconnect, or disconnect the site in PayHelm Integrations and try again.',
          )
          setStatus('error')
          return
        }
        if (code === 'owned_by_other') {
          setError(
            data?.message ||
              'This site is linked to another PayHelm account. Ask the owner to disconnect or transfer it in PayHelm.',
          )
          setStatus('error')
          return
        }
        setError(
          (typeof code === 'string' ? code : null) ||
            data?.message ||
            err?.message ||
            'Unable to link this WordPress site. Please try again or contact support.',
        )
        setStatus('error')
      }
    },
    [payload],
  )

  useEffect(() => {
    runClaim()
  }, [runClaim])

  const linkToOrg = async (org: OrgSummary) => {
    setStatus('claiming')
    try {
      // Switch into the org so subsequent verify/session matches workspace ownership.
      await axios.post(
        `${baseUrl}/organizations/switch`,
        { organization_id: org.id },
        getAxiosDefaultConfig(),
      )
    } catch (e) {
      console.warn('org switch before claim failed; claiming with organization_id anyway', e)
    }
    await runClaim(org.id)
  }

  const storeLabel = payload?.store_url || 'this WordPress site'

  return (
    <div style={{ maxWidth: 520, margin: '64px auto', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ marginTop: 0 }}>
        {payload?.reconnect ? 'Reconnect this WordPress site' : 'Link this WordPress site'}
      </h2>
      <p style={{ color: '#444', lineHeight: 1.5 }}>
        {payload?.reconnect
          ? 'Your plugin key changed (often after reinstall). Sign in as the PayHelm owner of this site to securely reconnect.'
          : (
            <>
              PayHelm site tools work without WooCommerce. Sign in to your PayHelm account to link{' '}
              <strong>{storeLabel}</strong>. This does not use WooCommerce authorization.
            </>
          )}
      </p>

      {status === 'loading' || status === 'claiming' || status === 'reconnecting' ? (
        <p>{status === 'reconnecting' ? 'Reconnecting…' : 'Linking store…'}</p>
      ) : null}

      {status === 'need_login' ? (
        <div>
          <p>You need to sign in to PayHelm to finish linking this site.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openLoginTopLevel({ redirect: true })}
          >
            Sign in to PayHelm
          </button>
        </div>
      ) : null}

      {status === 'need_org_switch' ? (
        <div>
          <p style={{ color: '#444', lineHeight: 1.5 }}>
            Choose which organization should own <strong>{storeLabel}</strong>. Linking to an
            organization makes the site available to everyone in that workspace.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
            {organizations.map((org) => (
              <button
                key={org.id}
                type="button"
                className="btn btn-primary"
                onClick={() => linkToOrg(org)}
              >
                Link to {org.name}
              </button>
            ))}
          </div>
          <p style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => openPayhelmTopLevel('/dashboard')}
            >
              Open PayHelm
            </button>
          </p>
        </div>
      ) : null}

      {status === 'error' ? (
        <div>
          <p style={{ color: '#b00020' }}>{error}</p>
          <button
            type="button"
            className="btn btn-default"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
          {' '}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openLoginTopLevel({ redirect: true })}
          >
            Sign in with a different account
          </button>
        </div>
      ) : null}

      {status === 'done' ? <p>Linked. Opening PayHelm…</p> : null}
    </div>
  )
}
